import { formatAsFilename, formatMessage, formatSubject } from "../helperFunctions/pdfFunctions";
import { setEmailTemplate, setupTemplateVariables } from "../helperFunctions/templateHelpers";
import { getWorkflow, inferWorkflowKeyFromJobDetails } from "../helperFunctions/workflows";

export const APPLY_QUOTE_NUMBER = "APPLY_QUOTE_NUMBER";

// Effects Multiple Fields Actions
export const TOGGLE_PDF_TYPE = "TOGGLE_PDF_TYPE";
export const TOGGLE_IS_PAID = "TOGGLE_IS_PAID";
export const TOGGLE_BOTH = "TOGGLE_BOTH";
export const TOGGLE_REVIEW = "TOGGLE_REVIEW";
export const SET_EMAIL_SENT_STATUS = "SET_EMAIL_SENT_STATUS";
export const TOGGLE_PDF_MENU = "TOGGLE_PDF_MENU";
export const TOGGLE_EMAIL_MENU = "TOGGLE_EMAIL_MENU";
export const SELECT_WORKFLOW = "SELECT_WORKFLOW";
// export const TOGGLE_ADDRESS_IN_SUBJECT = "TOGGLE_ADDRESS_IN_SUBJECT";

// Effects Single Field Actions
export const TOGGLE_MATERIALS = "TOGGLE_MATERIALS";
export const TOGGLE_VAT_BREAKDOWN = "TOGGLE_VAT_BREAKDOWN";
export const TOGGLE_BREAKDOWN = "TOGGLE_BREAKDOWN";
export const TOGGLE_DISCOUNTS = "TOGGLE_DISCOUNTS";
export const SET_EMAIL_MESSAGE = "SET_EMAIL_MESSAGE";
export const SET_EMAIL_SUBJECT = "SET_EMAIL_SUBJECT";
export const SET_EMAIL_SEND_TO = "SET_EMAIL_SEND_TO";
export const SET_EMAIL_ATTACHMENTS = "SET_EMAIL_ATTACHMENTS";

export const initialPdfState = {
  pdfType: "Invoice",
  showMaterials: false,
  showBreakdown: false,
  showDiscounts: false,
  showVatBreakdown: false,
  showAddressInSubject: true,
  subject: "",
  workflowKey: "invoice_general",
  subjectPrefix: "",
  messageType: "General (Invoice)",
  message: "",
  attachments: [],
  showReview: false,
  showEmailMenu: false,
  showPdfMenu: false,
  isPaid: false,
  both: false,
  sendTo: "",
  sending: false,
  emailSentStatus: "",
  draftUrl: null,
};

export const pdfPageReducer = (state, action) => {
  switch (action.type) {
    case TOGGLE_PDF_TYPE: {
      // pdfType specific updates
      let newPdfType = state.pdfType === "Invoice" ? "Quote" : "Invoice";
      let { amount, addressLine } = action;

      let currentWorkflow = getWorkflow(state.workflowKey);
      let newWorkflowKey =
        currentWorkflow?.pdfType === newPdfType
          ? state.workflowKey
          : inferWorkflowKeyFromJobDetails(state.timesheet?.jobDetails, newPdfType);
      let newWorkflow = getWorkflow(newWorkflowKey);

      let subject = formatSubject({
        ...state,
        isPaid: newPdfType === "Quote" ? false : state.isPaid,
        pdfType: newPdfType,
        subjectPrefix: newWorkflow.subjectPrefix,
      });

      let message = formatMessage({
        ...state,
        isPaid: false,
        showReview: false,
        addressLine,
        pdfType: newPdfType,
        amount,
        workflowKey: newWorkflowKey,
        messageType: newWorkflow.messageType,
      });

      const updates = {
        pdfType: newPdfType,
        workflowKey: newWorkflowKey,
        subjectPrefix: newWorkflow.subjectPrefix,
        messageType: newWorkflow.messageType,
        subject: subject,
        message: message,
        isPaid: false,
        showReview: false,
      };

      return { ...state, ...updates };
    }
    case SELECT_WORKFLOW: {
      let { workflowKey, amount, addressLine } = action.payload;
      let workflow = getWorkflow(workflowKey);

      if (!workflow) return state;

      let subject = formatSubject({
        ...state,
        subjectPrefix: workflow.subjectPrefix,
      });

      let message = formatMessage({
        ...state,
        addressLine,
        amount,
        workflowKey,
        messageType: workflow.messageType,
      });

      let updates = {
        workflowKey,
        subjectPrefix: workflow.subjectPrefix,
        messageType: workflow.messageType,
        subject,
        message,
      };

      return { ...state, ...updates };
    }
    case TOGGLE_IS_PAID: {
      let { amount, addressLine } = action.payload;

      let updatedIsPaid = !state.isPaid;

      let updatedMessage = formatMessage({
        ...state,
        isPaid: updatedIsPaid,
        addressLine,
        amount,
      });
      let updatedSubject = formatSubject({
        pdfType: state.pdfType,
        isPaid: updatedIsPaid,
        subjectPrefix: state.subjectPrefix,
        timesheet: state.timesheet,
      });

      let updates = {
        isPaid: updatedIsPaid,
        message: updatedMessage,
        subject: updatedSubject,
        both: false,
      };

      return { ...state, ...updates };
    }
    case TOGGLE_BOTH: {
      let { amount, addressLine } = action.payload;

      let updatedSendBoth = !state.both;

      let updatedMessage = formatMessage({
        ...state,
        addressLine,
        amount,
      });
      let updatedSubject = formatSubject({
        pdfType: state.pdfType,
        subjectPrefix: state.subjectPrefix,
        timesheet: state.timesheet,
        sendBoth: updatedSendBoth,
      });

      let updates = {
        message: updatedMessage,
        subject: updatedSubject,
        both: updatedSendBoth,
        isPaid: updatedSendBoth === true && false,
      };

      return { ...state, ...updates };
    }
    case TOGGLE_REVIEW: {
      let { amount, addressLine } = action.payload;

      let updatedShowReview = !state.showReview;
      let updatedMessage = formatMessage({
        ...state,
        showReview: updatedShowReview,
        addressLine,
        amount,
      });

      let updates = {
        showReview: updatedShowReview,
        message: updatedMessage,
      };

      return { ...state, ...updates };
    }
    case TOGGLE_MATERIALS: {
      return { ...state, showMaterials: !state.showMaterials };
    }
    case TOGGLE_BREAKDOWN: {
      return { ...state, showBreakdown: !state.showBreakdown };
    }
    case TOGGLE_DISCOUNTS: {
      return { ...state, showDiscounts: !state.showDiscounts };
    }
    case TOGGLE_VAT_BREAKDOWN: {
      return { ...state, showVatBreakdown: !state.showVatBreakdown };
    }
    case SET_EMAIL_MESSAGE: {
      return { ...state, message: action.payload };
    }
    case SET_EMAIL_SEND_TO: {
      let { value = state.sendTo, appendEmail } = action.payload;

      if (appendEmail && state.sendTo) {
        value = state.sendTo.concat(`, ${appendEmail}`);
      } else if (appendEmail) {
        value = appendEmail;
      }

      return { ...state, sendTo: value };
    }
    case SET_EMAIL_SUBJECT: {
      return { ...state, subject: action.payload };
    }
    case SET_EMAIL_SENT_STATUS: {
      // Any status change clears the previous draft link unless this payload sets a new one
      // (the successful-create-draft case) - so a stale "Draft created" link never survives
      // into an unrelated status like "Sending..." or an error.
      return { ...state, draftUrl: null, ...action.payload };
    }
    case SET_EMAIL_ATTACHMENTS: {
      let updates = [...state.attachments, ...action.payload];
      return { ...state, attachments: updates };
    }
    case TOGGLE_EMAIL_MENU: {
      let newShowEmailMenu = !state.showEmailMenu;
      // let newShowPdfMenu = newShowEmailMenu ? false : false

      let updates = {
        showEmailMenu: newShowEmailMenu,
        showPdfMenu: false,
        attachments: [],
      };

      return { ...state, ...updates };
    }
    case APPLY_QUOTE_NUMBER: {
      let updatedTimesheet = {
        ...state.timesheet,
        refNumber: action.payload,
      };

      let updatedSubject = formatSubject({
        ...state,
        timesheet: updatedTimesheet,
      });

      let updates = {
        subject: updatedSubject,
        timesheet: updatedTimesheet,
      };
      return { ...state, ...updates };
    }
    case TOGGLE_PDF_MENU: {
      let newShowPdfMenu = !state.showPdfMenu;
      // let newShowPdfMenu = newShowEmailMenu ? false : false

      let updates = {
        showEmailMenu: false,
        showPdfMenu: newShowPdfMenu,
        attachments: [],
      };

      return { ...state, ...updates };
    }
    // case TOGGLE_ADDRESS_IN_SUBJECT: {
    //   let subjectOptions = {
    //     pdfType: state.pdfType,
    //     showAddressInSubject: !state.showAddressInSubject,
    //   };

    //   let subject = formatSubject(subjectOptions, action.timesheet);
    //   // let filename = formatAsFilename(subject);

    //   let updates = {
    //     showAddressInSubject: !state.showAddressInSubject,
    //     subject: formatSubject(subjectOptions, action.timesheet),
    //   };

    //   return {
    //     ...state,
    //     ...updates,
    //   };
    // }
    default: {
      console.log("in default pdf reducer");
      return state;
    }
  }
};
